import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useMyInfo } from '../apollo/auth'
import { orpcClient } from './client'
import {
  extraPackageBoostListMyBoostsOptions,
  extraPackageBoostListMyBoostsQueryKey,
} from './generated/@tanstack/react-query.gen'
import type { ExtraPackageBoostListMyBoostsResponse } from './generated/index'

const BOOST_FETCH_LIMIT = 50

const BOOST_TEMPLATE = {
  name: 'membership:ticket.template.extra-package-boost.name',
  description: 'membership:ticket.template.extra-package-boost.description',
} as const

// Transforms raw API response: keeps time fields as strings (timestamp),
// and injects client-side `template` metadata not present in the API response.
function selectBoosts(data: ExtraPackageBoostListMyBoostsResponse) {
  return data.data
    .map(d => ({
      ...d,
      template: BOOST_TEMPLATE,
    }))
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )
}

export type BoostItem = ReturnType<typeof selectBoosts>[number]

export function useMyAvailableBoosts() {
  const { data: me } = useMyInfo()
  return useQuery({
    ...extraPackageBoostListMyBoostsOptions({
      client: orpcClient,
      query: { status: ['available', 'locked'], first: BOOST_FETCH_LIMIT },
    }),
    enabled: Boolean(me),
    select: selectBoosts,
  })
}

export function useMyHistoryBoosts({
  enabled = true,
}: { enabled?: boolean } = {}) {
  const { data: me } = useMyInfo()
  return useQuery({
    ...extraPackageBoostListMyBoostsOptions({
      client: orpcClient,
      query: { status: ['redeemed', 'expired'], first: BOOST_FETCH_LIMIT },
    }),
    enabled: Boolean(me) && enabled,
    select: selectBoosts,
  })
}

// Must pass { client: orpcClient } so the generated query key includes the
// correct baseUrl. Without it, baseUrl defaults to the auto-generated client
// (empty) and won't match the actual query keys created by useMyAvailableBoosts.
const boostQueryKeyFilter = () =>
  extraPackageBoostListMyBoostsQueryKey({ client: orpcClient })

export function useInvalidateBoosts() {
  const queryClient = useQueryClient()
  return useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: boostQueryKeyFilter(),
      }),
    [queryClient],
  )
}

export function useRemoveBoostQueries() {
  const queryClient = useQueryClient()
  return useCallback(
    () =>
      queryClient.removeQueries({
        queryKey: boostQueryKeyFilter(),
      }),
    [queryClient],
  )
}

export function useResetBoostQueries() {
  const queryClient = useQueryClient()
  return useCallback(
    () =>
      queryClient.resetQueries({
        queryKey: boostQueryKeyFilter(),
      }),
    [queryClient],
  )
}
